import { Box, Stack, Typography } from '@mui/material'
import { DateTime } from 'luxon'
import { FC, useMemo, useState } from 'react'
import { ModerationEvent } from '../../../../api/models'
import { groupAndSortEvents } from '../../../../utils/groupEventsByDate'
import { bundleDuplicateClearanceEvents, getMostRecentFailedEventId } from '../cameraOverviewUtils'
import { CameraHistoryItem } from './CameraHistoryItem'

interface CameraHistoryListProps {
  events: ModerationEvent[]
}

export const CameraHistoryList: FC<CameraHistoryListProps> = ({ events }) => {
  const bundledEvents = useMemo(() => bundleDuplicateClearanceEvents(events), [events])
  const groupedEvents = groupAndSortEvents(bundledEvents)
  const [expandedId, setExpandedId] = useState<string | null>(() => getMostRecentFailedEventId(bundledEvents) ?? null)

  const handleCardOpeningToggle = (id: string, isExpanded: boolean) => {
    setExpandedId(isExpanded ? id : null)
  }

  return (
    <Stack>
      <Typography data-testid="camera-history-heading" sx={{ fontSize: 'var(--size-m)', fontWeight: 500, mb: 2 }}>
        Camera History
      </Typography>

      {Object.entries(groupedEvents).map(([date, dayEvents]) => (
        <Box key={date} sx={{ position: 'relative', mb: 1, display: 'flex', justifyContent: 'flex-start' }}>
          {/* Date Header */}
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
              position: 'absolute',
              width: 80,
              left: 15
            }}
          >
            {date}
          </Typography>

          <Box sx={{ position: 'relative' }}>
            {/* Vertical Line */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 100,
                width: '2px',
                height: '100%',
                backgroundColor: 'var(--border-high)',
                zIndex: 0
              }}
            />

            {/* Events */}
            <Stack spacing={1} sx={{ pl: 0 }}>
              {dayEvents.map((event, i) => {
                const eventTime = DateTime.fromISO(event.createdAt.toString()).toFormat('h:mm a')
                return (
                  <Box
                    key={event.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    {/* Time */}
                    <Typography
                      sx={{
                        width: 60,
                        textAlign: 'right',
                        flexShrink: 0,
                        position: 'relative',
                        left: 'var(--spacing-m)'
                      }}
                      variant="body2"
                    >
                      {eventTime}
                    </Typography>

                    {/* Dot over line */}
                    <Box
                      sx={{
                        width: '0.875rem',
                        height: 'var(--spacing-xs)',
                        borderRadius: '6.25rem',
                        backgroundColor: 'var(--border-high)',
                        border: '2px solid var(--bg-surface-low)',
                        zIndex: 2,
                        position: 'relative',
                        left: '2px'
                      }}
                    />

                    {/* Card */}
                    <CameraHistoryItem
                      data-testid="camera-history-item"
                      event={event}
                      expanded={expandedId === event.id}
                      onToggle={isExpanded => handleCardOpeningToggle(event.id, isExpanded)}
                    />
                  </Box>
                )
              })}
            </Stack>
          </Box>
        </Box>
      ))}
    </Stack>
  )
}
